/**
 * Тумблеры и константы приложения. Меняются здесь и нигде больше.
 */
export type ApiMode = 'mock' | 'live';

function resolveApiMode(): ApiMode {
  const explicit = import.meta.env.VITE_API_MODE;
  if (explicit === 'mock' || explicit === 'live') return explicit;
  // Пока бэкенд §14 не готов, dev-сборка по умолчанию работает на mock-адаптере.
  return import.meta.env.DEV ? 'mock' : 'live';
}

export const appConfig = {
  /** Префикс `/app/` — единственный источник vite.config.ts (NFR-1). */
  basePath: import.meta.env.BASE_URL,
  apiUrl: import.meta.env.VITE_API_URL ?? 'https://shotbook.art/api',
  apiMode: resolveApiMode(),
  googleClientId: import.meta.env.VITE_GOOGLE_CLIENT_ID ?? '',
  crispWebsiteId: import.meta.env.VITE_CRISP_WEBSITE_ID ?? '',
  appBuild: import.meta.env.VITE_APP_BUILD ?? 'dev',

  siteUrl: 'https://shotbook.art',
  supportEmail: 'support@shotbook.art',
  /** FAQ лендинга — пункт «FAQ on the site» в окне помощи (NAV-6). */
  faqUrl: 'https://shotbook.art/#faq',
  /** Условия — ссылка под формой регистрации (siteConfig.legal.terms лендинга). */
  termsUrl: 'https://shotbook.art/terms',

  /** Каталог: 24 на страницу, подгрузка за 400px (README). */
  catalogPageSize: 24,
  catalogPrefetchPx: 400,
  searchDebounceMs: 300,
  /** С трёх символов: короче бэкенд не дописывает обрывок слова, выдача — шум (ADR-009). */
  searchMinLength: 3,
  /** Пауза после ввода, после которой запрос считается отправленным (двухфазный поиск, ADR-009). */
  searchSubmitPauseMs: 600,
  /** Смена фильтра/запроса держит прежнюю выдачу приглушённой не меньше этого времени — без мигания на быстрых ответах. */
  catalogRefreshMinMs: 450,
} as const;

/**
 * Ключи хранилищ. `shotbook_token`/`shotbook_user` — контракт с текущим SPA
 * (AUTH-9); `auth_token` на этом origin принадлежит админке и не читается.
 */
export const storageKeys = {
  token: 'shotbook_token',
  user: 'shotbook_user',
  /** dark | light; отсутствие ключа = системная тема. Ключ общий с лендингом. */
  theme: 'sb-theme',
  lang: 'shotbook_lang',
  catalogLayout: 'shotbook_catalog_layout',
  catalogSize: 'shotbook_catalog_size',
  /** Ширина панели разбора в px; тянется за левый край. */
  videoDrawerWidth: 'shotbook_video_drawer_width',
  /** Высота кадра в панели разбора в px; тянется за границу под сценой. */
  videoStageHeight: 'shotbook_video_stage_height',
  longPressHint: 'shotbook_hint_longpress',
  onboardingDone: 'shotbook_onboarding_done',
  pendingPlan: 'pending_subscription_plan',
  /** Вкладка экрана входа из `?action=register|login` (sessionStorage). */
  authTab: 'shotbook_auth_tab',
  paywallDockDismissed: 'shotbook_paywall_dock_dismissed',
  emptyRequestShown: 'shotbook_empty_request_shown',
  /** Бэкенд ответил поиском v3 — включаем двухфазный поиск (sessionStorage). */
  searchTwoPhase: 'shotbook_search_two_phase',
} as const;

/**
 * Локальные флаги интерфейса, которые чистятся при выходе (AUTH-10).
 * Предпочтения темы и языка — не флаги, остаются.
 */
export const uiFlagKeys: readonly string[] = [
  storageKeys.catalogLayout,
  storageKeys.catalogSize,
  storageKeys.longPressHint,
  storageKeys.onboardingDone,
  storageKeys.paywallDockDismissed,
  storageKeys.emptyRequestShown,
];
